import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  computed,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ChartData, ChartOptions } from 'chart.js';
import { LoginService } from '../login.service';
import { ExamsService } from '../exams.service';
import { PatientsService } from '../patients.service';
import { DashboardService, DoctorOption, ExamStatsApi, RecentExamApi } from '../dashboard.service';
import {
  EXAM_TYPE_SHORT,
  EXAM_TYPE_TAG,
  ExamSlice,
  RecentExam,
  SLICE_COLORS,
} from '../interfaces/dashboard.component';

@Component({
  selector: 'app-dashboard-exam-breakdown',
  standalone: false,
  templateUrl: './dashboard-exam-breakdown.html',
  styleUrl: './dashboard-exam-breakdown.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardExamBreakdownComponent implements OnInit {
  @Input() loading = false;
  @Input() separatedMode = false;

  readonly examPeriod = signal<'today' | 'week' | 'month'>('week');
  readonly examTotal = signal(0);
  readonly examPrevPeriodTotal = signal(0);
  readonly examSlices = signal<ExamSlice[]>([]);
  readonly recentExams = signal<RecentExam[]>([]);
  // Own load state: this widget fetches its own stats, so it can't rely on the
  // parent's dashboard-level `loading` (a separate call that may finish first).
  readonly loadingExams = signal(true);
  // Server tells us whether the counts are clinic-wide or scoped to this doctor.
  readonly scope = signal<'clinic' | 'user'>('clinic');
  // Doctor filter: whether this user may filter, the selectable openers, and the
  // current selection (null = all doctors). Availability is decided server-side.
  readonly canFilterByDoctor = signal(false);
  readonly doctors = signal<DoctorOption[]>([]);
  readonly selectedDoctor = signal<number | null>(null);
  // Bumped on every language change so the instant()-based trend re-translates.
  private readonly lang = signal('');

  readonly examTrend = computed(() => {
    const curr = this.examTotal();
    const prev = this.examPrevPeriodTotal();
    if (curr === 0 && prev === 0) return null;
    const diff = curr - prev;
    const pct = prev > 0 ? Math.round((diff / prev) * 100) : null;
    const tone = diff > 0 ? ('up' as const) : diff < 0 ? ('down' as const) : ('flat' as const);
    const sign = diff > 0 ? '+' : '';
    const pctStr = pct !== null ? ` (${pct > 0 ? '+' : ''}${pct}%)` : '';
    this.lang(); // re-translate when the interface language changes
    const phrase = this.translate.instant('GUI.dashboard.labels.chips.exams vs last week');
    // Keep the numeric run (`-3 (-100%)`) separate so the template can isolate
    // it in a <bdi>; otherwise its sign/parens get reordered inside an RTL line.
    return { tone, stats: `${sign}${diff}${pctStr}`, phrase };
  });

  readonly donutHoleLabel = computed(() => {
    this.lang(); // re-translate when the interface language changes
    const p = this.examPeriod();
    if (p === 'today') return this.translate.instant('GUI.dashboard.selects.today');
    if (p === 'week') return this.translate.instant('GUI.dashboard.labels.last 7 days');
    return this.translate.instant('GUI.dashboard.selects.this month');
  });

  readonly donutChartData = computed<ChartData<'doughnut'>>(() => ({
    labels: this.examSlices().map((s) => s.label),
    datasets: [
      {
        data: this.examSlices().map((s) => s.value),
        backgroundColor: this.examSlices().map((s) => s.color),
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  }));

  readonly donutChartOptions: ChartOptions<'doughnut'> = {
    responsive: false,
    cutout: '72%',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.formattedValue}`,
        },
      },
    },
  };

  constructor(
    private dashboardService: DashboardService,
    private loginService: LoginService,
    private patientsService: PatientsService,
    private examService: ExamsService,
    private translate: TranslateService
  ) {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe((e) => this.lang.set(e.lang));
  }

  ngOnInit(): void {
    this.fetchStats();
  }

  onExamPeriodChange(period: string): void {
    this.examPeriod.set(period as 'today' | 'week' | 'month');
    this.fetchStats();
  }

  onDoctorChange(value: string): void {
    this.selectedDoctor.set(value ? Number(value) : null);
    this.fetchStats();
  }

  // Reload stats for the current period + doctor selection.
  private fetchStats(): void {
    this.loadingExams.set(true);
    this.dashboardService.getExamStats(this.examPeriod(), this.selectedDoctor()).subscribe({
      next: (data) => this.applyExamStats(data),
      error: () => this.loadingExams.set(false),
    });
  }

  openExam(exam: RecentExam): void {
    this.patientsService.getPatient(exam.pid, (s: unknown) => {
      if (s) {
        this.examService.goExam(null, { AutoIndex: exam.examIndex }, exam.type, exam.viewOnly);
      }
    });
  }

  private applyExamStats(data: ExamStatsApi): void {
    this.loadingExams.set(false);
    this.scope.set(data.scope);
    this.canFilterByDoctor.set(data.canFilterByDoctor);
    this.doctors.set(data.doctors ?? []);
    this.examTotal.set(data.examTotal);
    this.examPrevPeriodTotal.set(data.examPrevPeriodTotal);
    this.examSlices.set(
      data.examSlices.map((s, i) => ({
        ...s,
        color: SLICE_COLORS[i % SLICE_COLORS.length],
        short: EXAM_TYPE_SHORT[s.label] ?? s.label.split(' ')[0],
      }))
    );
    this.recentExams.set(this.mapRecentExams(data.recentExams));
  }

  private mapRecentExams(data: RecentExamApi[]): RecentExam[] {
    const login = this.loginService;
    const canEdit =
      login.userType === 'doctor' || login.userType === 'visitingdoc' || login.userType === 'tech';
    return data.map((r) => {
      const isClosed = this.isExamClosed(r.CreationTime, r.ClosedDate);
      const restrictedByClinic =
        login.separatedClinic &&
        !login.isAdmin &&
        login.userType !== 'secretary' &&
        r.UID !== login.doctor;
      const viewOnly = isClosed || !canEdit || restrictedByClinic;
      return {
        initials: `${r.First?.[0] ?? ''}${r.Last?.[0] ?? ''}`.toUpperCase(),
        name: `${r.First} ${r.Last}`,
        type: r.ExamType,
        typeTag: EXAM_TYPE_TAG[r.ExamType] ?? 'teal',
        when: this.formatRelativeTime(new Date(r.CreationTime)),
        pid: r.PID,
        examIndex: r.AutoIndex,
        viewOnly,
        openerName: r.OpenerName ?? null,
      };
    });
  }

  private isExamClosed(creationTime: string, closedDate: string | null): boolean {
    if (closedDate) return true;
    const now = new Date();
    const closure = new Date();
    closure.setHours(3, 0, 0, 0);
    if (now.getHours() < 3) closure.setDate(closure.getDate() - 1);
    return closure > new Date(creationTime);
  }

  private formatRelativeTime(date: Date): string {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((todayStart.getTime() - dateStart.getTime()) / (1000 * 60 * 60 * 24));
    // 24-hour clock for every language (no AM/PM).
    const timeStr = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    if (diffDays === 0) return `${this.translate.instant('GUI.dashboard.labels.today')} ${timeStr}`;
    if (diffDays === 1) return `Yesterday ${timeStr}`;
    return `${diffDays} days ago`;
  }
}
